import { computed, reactive, readonly, ref } from 'vue';
import { TempSearchApiClientService, TempSearchViewModel } from '@/shared/search/temp-search-api';
import siteContextStore from '@/shared/content/site-context.store';
import { debounce } from 'lodash';
import { hardRedirect } from '@/core/navigation/redirect.service';
import { QueryKeys } from '@/shared/config/route-query.config';
import navigationStore from '@/shared/navigation/navigation.store';
import useSearchTracking from '@/shared/tracking/useSearchTracking';

const { newSearchResult } = useSearchTracking();

const pending = ref<boolean>(false);
const term = ref<string>('');

const state: ISearchState = reactive({
    result: null,
});

const searchLink = siteContextStore.getSearchPageLink();

export default function useSearch() {
    const searchPageLink = computed((): string => {
        return `${searchLink.url}?${QueryKeys.Term}=${term.value}`;
    });

    const search = (searchTerm: string): void => {
        if (searchTerm === term.value) {
            return;
        }

        term.value = searchTerm;
        pending.value = true;
        searchDebounced(searchTerm);
    };

    const goToSearchPage = (): void => {
        hardRedirect(searchPageLink.value);
        navigationStore.toggleActiveMainNavigationItem(null);
        newSearchResult(term.value, result.value);
    };

    const searchInternal = async(searchTerm: string): Promise<TempSearchViewModel> => {
        const resultTemp = await TempSearchApiClientService.search(searchTerm);
        pending.value = false;
        state.result = resultTemp;
        newSearchResult(term.value, result.value);
        return resultTemp;
    };

    const result = computed((): TempSearchViewModel | null => {
        return term.value ? state.result : null;
    });

    const searchDebounced:(term: string) => void = debounce(searchInternal, 300);

    return {
        result,
        search,
        goToSearchPage,
        searchPageLink,
        term: readonly(term),
        pending: readonly(pending),
    };
}

interface ISearchState {
    result: TempSearchViewModel;
}
